import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Backpack, Plus, X, Send, Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { W, useEdition } from "@/lib/edition";

export const KitCard = () => {
  const { w } = useEdition();
  const [k, setK] = useState(null);
  const [rules, setRules] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/kit").then((r) => { setK(r.data ?? null); setRules(r.data?.rules ?? []); }).catch(() => setK(null)); }, []);

  const save = async (patch = {}) => {
    try {
      const { data } = await api.put("/kit", { enabled: k.enabled, rules, ...patch });
      setK(data); setRules(data?.rules ?? []);
      toast.success("Kit reminders saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const test = async () => {
    setBusy(true);
    try { const { data } = await api.post("/kit/send-test"); toast.success(`Reminder sent to ${data.to}`); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const edit = (i, field, v) => setRules((rs) => rs.map((r, j) => (j === i ? { ...r, [field]: v } : r)));

  if (!k) return null;
  const items = k.tomorrow?.items ?? [];
  const cls = "min-w-0 flex-1 rounded-lg border border-slate-300 bg-surface px-3 py-2 text-sm outline-none focus:border-teal-600";
  return (
    <article data-testid="kit-card" className="min-w-0 rounded-2xl bg-surface border border-slate-200 p-5 sm:p-6 lg:col-span-2">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-amber-50 text-amber-600"><Backpack className="w-5 h-5" /></span>
          <div><h3 className="font-semibold text-slate-900"><W s="Kit reminders" c="Bring-to-work reminders" /></h3><p className="text-sm text-slate-500"><W s="Email at 19:00 the night before (Sun–Thu) when tomorrow needs kit." c="Email at 19:00 the night before a workday when you need to bring something." /></p></div>
        </div>
        <Switch data-testid="kit-enabled-switch" checked={k.enabled} onCheckedChange={(v) => save({ enabled: v })} />
      </div>
      <div data-testid="kit-tomorrow" className="mt-4 rounded-xl bg-amber-50/60 border border-amber-100 p-3 text-sm text-slate-700">
        <b className="text-slate-900">Tomorrow ({k.tomorrow?.plan?.day}{k.tomorrow?.plan?.week ? `, Week ${k.tomorrow.plan.week}` : ""}):</b>{" "}
        {items.length ? items.map((i) => i.item).join(", ") : "nothing extra to pack"}
      </div>
      <p className="mt-5 font-mono text-[11px] uppercase tracking-wider text-slate-500"><W s="If a lesson includes… → pack" c="If a meeting includes… → bring" /></p>
      <ul className="mt-2 space-y-2">
        {rules.map((r, i) => (
          <li key={i} className="flex items-center gap-2">
            <input data-testid={`kit-rule-match-${i}`} value={r.match} onChange={(e) => edit(i, "match", e.target.value)} placeholder={w("Lesson word, e.g. Games", "Meeting word, e.g. Client")} className={cls} />
            <span className="text-slate-400">→</span>
            <input data-testid={`kit-rule-item-${i}`} value={r.item} onChange={(e) => edit(i, "item", e.target.value)} placeholder={w("Item, e.g. Games kit", "Item, e.g. Laptop charger")} className={cls} />
            <button data-testid={`kit-rule-remove-${i}`} onClick={() => setRules((rs) => rs.filter((_, j) => j !== i))} aria-label="Remove rule" className="grid place-items-center w-9 h-9 shrink-0 rounded-lg border border-slate-200 text-slate-500 hover:bg-rose-600 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex flex-wrap gap-2">
        <button data-testid="kit-add-rule" onClick={() => setRules((rs) => [...rs, { match: "", item: "" }])} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"><Plus className="w-4 h-4" /> Add rule</button>
        <button data-testid="kit-save" onClick={() => save()} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-black hover:bg-slate-700">Save rules</button>
        <button data-testid="kit-send-test" onClick={test} disabled={busy} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send tonight's reminder now</button>
      </div>
    </article>
  );
};
